import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Link, useParams, useLocation, useNavigate} from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import { io } from 'socket.io-client';
import { useQuery } from '@tanstack/react-query';
import ImageWithLoader2 from './components/ImageWithLoader2';

const COLORS = {
  dark: '#75B437',
  accent: '#75B437',
  light: '#EBF4ED',
};
const isLocalhost = window.location.hostname === 'localhost';
const SOCKET_URL = isLocalhost ? "http://localhost:8081" : "https://oret.africa";

const socket = io(SOCKET_URL, {
  path: "/tree/backend/socket.io",
  transports: ["polling", "websocket"],
  withCredentials: true,
  autoConnect: false
});


function Chats() {
  const navigate = useNavigate();
  const location = useLocation();
  const receiverId = location.state?.receiverId;

  const { menu, toggleMobileMenu } = useAppLayout();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [userId, setUserId] = useState(null);
  const [isReceiverOnline, setIsReceiverOnline] = useState(false);
  const messageEndRef = useRef(null);

  // Load userId from localStorage
  useEffect(() => {
    const id = localStorage.getItem('user_id');
    if (id) setUserId(id);
  }, []);

  // Receiver info
  const { data: receiverInfo } = useQuery({
    queryKey: ['receiver-info', receiverId],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/chat-member-receiver/${receiverId}`);
      return res.data;
    },
    enabled: !!receiverId,
  });

  // Fetch initial messages
  useEffect(() => {
    axios.get(`${API_URL}/chat-messages/${receiverId}`)
      .then(res => setMessages(res.data))
      .catch(err => console.error(err));
  }, [receiverId]);

  // Setup socket
  useEffect(() => {
    if (!userId) return;

    socket.connect();
    socket.emit("join", userId);
    socket.emit("check_online_status", receiverId);

    const handleMessage = async (data) => {
      setMessages(prev => [...prev, data]);

      // Auto-mark messages from receiver as read
      if (String(data.sender_id) === receiverId) {
        const token = localStorage.getItem('token');
        try {
          await axios.put(`${API_URL}/mark-as-read/${receiverId}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (err) {
          console.error('Failed to mark as read (real-time):', err);
        }
      }
    };

    const handleOnlineStatus = ({ userId: onlineUserId, isOnline }) => {
      if (String(onlineUserId) === receiverId) {
        setIsReceiverOnline(isOnline);
      }
    };

    socket.on("receive_message", handleMessage);
    socket.on("user_online_status", handleOnlineStatus);

    return () => {
      socket.off("receive_message", handleMessage);
      socket.off("user_online_status", handleOnlineStatus);
      socket.disconnect();
    };
  }, [userId, receiverId]);

  // Mark messages as read on open
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.put(`${API_URL}/mark-as-read/${receiverId}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(err => console.error('Failed to mark as read:', err));
  }, [receiverId]);

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim()) return;
  
    const messageData = {
      sender_id: userId,
      receiver_id: receiverId,
      message: newMessage
    };
  
    socket.emit("send_message", messageData);
  
    try {
      const res = await axios.post(`${API_URL}/send-message`, messageData);
  
      // Use the actual message object inside res.data.message
      const savedMessage = res.data.message;
  
      setMessages(prev => [...prev, savedMessage]);
      setNewMessage('');
    } catch (err) {
      console.error("Failed to send message:", err);
    }
  };
// end Send message


//delete message
  const deleteMessage = async (id) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API_URL}/delete-message/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { receiver_id: receiverId }, // optional if used for socket emit
      });
      setMessages(prev => prev.filter(msg => msg.id !== id));
    } catch (err) {
      console.error("Failed to delete message:", err);
    }
  };

  useEffect(() => {
    socket.on("message_deleted", ({ id }) => {
      setMessages(prev => prev.filter(msg => msg.id !== id));
    });
  
    return () => {
      socket.off("message_deleted");
    };
  }, []);

  //end delete message


  // Scroll to bottom on new messages
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className={`main-wrapper ${menu ? 'slide-nav': ''}`}>
    <Header onMenuClick={(value) => toggleMobileMenu()} />
    <Sidebar />

        <div className='page-wrapper'>
          <div className='content container-fluid'>
            <div className='row'>
              <div className='col-1 col-md-1'>

                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    aria-label="Back"
                    title="Back"
                    style={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      border: 'none',
                      background: COLORS.light,
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M15 6L9 12L15 18" stroke={COLORS.dark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>

              </div>
              <div className='col-10 col-md-10'>
                {receiverInfo && (
                  <div className="d-flex align-items-center mb-3">
                     

                    <ImageWithLoader2
                        src={receiverInfo.image_url}
                        placeholder="https://oret.africa/tree/img/imageplaceholder-icon.jpg"
                        fadeDuration={800}
                        blurAmount={15}
                        alt={`${receiverInfo.name} ${receiverInfo.last_name}`}
                        style={{
                          width: 44, height: 44, borderRadius: '50%', objectFit: 'cover',
                          border: `2px solid ${COLORS.light}`,
                        }}
                        onError={(e) => { e.target.src = '/images/default-avatar.png'; }}
                      />


                    <div style={{marginLeft: '2%'}}>
                      <h6 className='mb-0'>
                        {receiverInfo.n_prefix} {receiverInfo.name} {receiverInfo.last_name}
                      </h6>
                      <small style={{ color: isReceiverOnline ? 'green' : 'gray' }}>
                        {isReceiverOnline ? 'Online' : 'Offline'}
                      </small>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row'>
              <div className='col-md-12 chat-window'>
                <div className='chat-messages' style={{
                  maxHeight: '58vh',
                  overflowY: 'auto',
                  padding: '10px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #ddd'
                }}>
                  {messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`message-bubble ${String(msg.sender_id) === userId ? 'sent' : 'received'}`}
                    >
                      <p>{msg.message}</p>
                      <small>
                        {new Date(msg.created_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </small>

                         {/* Only show delete for sender */}
                        {String(msg.sender_id) === userId && (
                         
                            <>
                            <div className='row'>
                              <div className='col-10 col-md-10'></div>
                              <div className='col-2 col-md-2'>
                              <div className="dropstart">
                            <a href="#" role="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                style={{ color: '#184A2C' }}
                            >
                                <i className="fas fa-ellipsis-h"></i>
                            </a>
                            <ul className="dropdown-menu">
                                <li>
                                <button className="dropdown-item" onClick={() => deleteMessage(msg.id)}>
                                    <i className="fas fa-trash fa-fw me-2"></i>Delete
                                </button>
                                </li>
                            </ul>
                            </div>
                              </div>
                            </div>
                          
                            </>

                        )}

                    </div>
                  ))}
                  <div ref={messageEndRef}></div>
                </div>
              </div>
            </div>

            <div className='row'>
              <div className='col-md-12'>
                <div className='chat-input mt-3 d-flex' style={{
                    position: 'sticky',
                    bottom: 0,
                    background: '#fff',
                    padding: '10px',
                    zIndex: 1000
                  }}>

                <input
                        type='text'
                        className='form-control me-2'
                        placeholder='Type a message...'
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                        onFocus={() => {
                          // Ensure the input is visible when keyboard opens
                          setTimeout(() => {
                            messageEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                          }, 300);
                        }}
                      />
              
                  <button className='btn btn-primary' onClick={sendMessage}>Send</button>
                </div>
              </div>
            </div>

          
          </div>
        </div>
      </div>
    
  );
}

export default Chats;
